<script setup lang="ts">
import { useTextCut } from '~/composables/useTextCut'
import type { IMoneyTransferCard } from '~/components/entity/money-transfer/types'
import type { IEntityPosition } from '~/components/entity/base-entity'
import { UiCardTop } from '~/components/ui/card'
import { Rating } from '~/components/ui/rating'

const props = withDefaults(
  defineProps<{
    item: IMoneyTransferCard
    hideOrganizationDetails?: boolean
    position?: IEntityPosition
  }>(),
  {
    hideOrganizationDetails: false,
    position: undefined
  }
)

const previewDescription = computed(() =>
  props.item.description ? useTextCut(props.item.description) : ''
)

const isHighlighted = useEntityHighlight(props.item, props.position)

// The title may carry the search highlight (<em>); attributes need plain text
const plainTitle = computed(() => useStripTags(props.item.title))
</script>

<template>
  <article :class="[isHighlighted ? 'CardPremium group' : 'Card']">
    <UiCardTop v-if="isHighlighted" />
    <div class="EntityCardLayout">
      <div class="flex gap-4">
        <NuxtLinkLocale
          v-if="!props.hideOrganizationDetails"
          :to="props.item.url"
          :aria-label="plainTitle"
        >
          <div class="relative md:flex md:h-20 md:w-28 md:flex-col md:items-start">
            <NuxtImg
              :src="props.item.logo"
              :alt="plainTitle"
              class="w-24 min-w-24 object-contain md:h-full md:w-full md:max-w-full"
            />
            <NuxtImg
              v-if="isHighlighted"
              src="/images/photostock/brilliant.png"
              width="24"
              height="24"
              :alt="$t('common.premium_marker_alt')"
              class="absolute bottom-[-6px] right-[-6px]"
              format="avif,webp"
            />
          </div>
        </NuxtLinkLocale>
        <div>
          <NuxtLinkLocale
            :to="props.item.url"
            class="inline-block hover:underline"
            :aria-label="$t('common.detailsAbout', { title: plainTitle })"
          >
            <Heading tag="h3" class="hit-title h6"><span v-hydrated-html="props.item.title" /></Heading>
          </NuxtLinkLocale>
          <p class="SecondaryTextHeadings">{{ previewDescription }}</p>
          <div v-if="props.item.ratings" class="flex items-center">
            <Rating :value="Number(props.item.ratings)" size="xs" />
            <div class="ml-2 text-sm text-gray-900">{{ props.item.ratings }}</div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-y-2">
        <div>
          <div class="SecondaryTextHeadings">
            {{ $t('entity.money-transfer.item.registration_number') }}
          </div>
          <p class="font-semibold">
            {{ props.item.registration_number_nbkr ?? '-' }}
          </p>
        </div>
        <div v-if="props.item.currencies">
          <div class="SecondaryTextHeadings">{{ $t('common.currency') }}</div>
          <div class="font-semibold">
            <span>{{ Object.values(props.item.currencies).join(', ') }}</span>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
